import { Sheet } from "../ui/Sheet";
import { t } from "../../i18n/strings";
import type { Provenance } from "../../engine/types";
import type { Locale } from "../../templates/types";

const SOURCE_LABEL_KEY: Record<Provenance["source"], "provenance.source.llmExtraction" | "provenance.source.humanEdit" | "provenance.source.engineDefault"> = {
  llm_extraction: "provenance.source.llmExtraction",
  human_edit: "provenance.source.humanEdit",
  engine_default: "provenance.source.engineDefault",
};

/**
 * Clicking any field card opens this — the quote, the turn it came from,
 * confidence, input mode, and whether a human edited it. This is the
 * feature that makes the demo credible to a research audience (§8).
 */
export function ProvenancePopover({
  open,
  onClose,
  provenance,
  locale = "en",
  fieldLabel,
}: {
  open: boolean;
  onClose: () => void;
  provenance: Provenance | null;
  locale?: Locale;
  /** Field label, for the popover's accessible name — e.g. "Provenance for Fruit & vegetable servings per day". */
  fieldLabel?: string;
}) {
  if (!open) return null;

  const ariaLabel = fieldLabel
    ? t(locale, "provenance.ariaLabelFor", { field: fieldLabel })
    : t(locale, "provenance.ariaLabelGeneric");

  return (
    <Sheet open={open} onClose={onClose} aria-label={ariaLabel}>
      {!provenance ? (
        <p className="text-sm text-ink-soft">{t(locale, "provenance.empty")}</p>
      ) : (
        <dl className="space-y-2 text-sm">
          <div>
            <dt className="font-medium text-ink-soft">{t(locale, "provenance.label.source")}</dt>
            <dd className="text-ink">{t(locale, SOURCE_LABEL_KEY[provenance.source])}</dd>
          </div>
          {provenance.quote && (
            <div>
              <dt className="font-medium text-ink-soft">{t(locale, "provenance.label.quote")}</dt>
              <dd className="italic text-ink">"{provenance.quote}"</dd>
            </div>
          )}
          {provenance.quoteTurnIndex !== null && (
            <div>
              <dt className="font-medium text-ink-soft">{t(locale, "provenance.label.turn")}</dt>
              <dd className="text-ink">#{provenance.quoteTurnIndex}</dd>
            </div>
          )}
          {provenance.confidence !== null && (
            <div>
              <dt className="font-medium text-ink-soft">{t(locale, "provenance.label.confidence")}</dt>
              <dd className="text-ink">{Math.round(provenance.confidence * 100)}%</dd>
            </div>
          )}
          <div>
            <dt className="font-medium text-ink-soft">{t(locale, "provenance.label.inputMode")}</dt>
            <dd className="text-ink">
              {provenance.inputMode === "voice" ? t(locale, "provenance.inputMode.voice") : t(locale, "provenance.inputMode.typed")}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-ink-soft">{t(locale, "provenance.label.reviewed")}</dt>
            <dd className="text-ink">{provenance.reviewed ? t(locale, "common.yes") : t(locale, "common.no")}</dd>
          </div>
        </dl>
      )}
    </Sheet>
  );
}
